"use client";

import { useState, useRef, Suspense, useEffect, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Edit,
  FileText,
  Share2,
  Check,
  Loader2,
  ChevronDown,
  Save,
  X,
  Wand2,
  Eye,
  Sparkles,
  Blocks,
  Image as ImageIcon,
  Edit3,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Plus,
  GripVertical,
  MoreHorizontal,
  Trash2,
  CheckCircle,
} from "lucide-react";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../foundation/altzero_base/base/components/ui/tabs";
import { Card, CardContent } from "../../../foundation/altzero_base/base/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../foundation/altzero_base/base/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Import components, types, and utilities
import {
  DocumentProvider,
  useDocumentContext,
} from "../contexts/DocumentContext";
import { SectionEditor } from "../components/SectionEditor";
import { ThemeControls } from "../components/ThemeControls";
import { PagedDocumentPreview } from "../components/PagedDocumentPreview";
import PDFLikePreview from "../components/PDFLikePreview";
import SectionBasedEditor from "../components/documents/generated/SectionBasedEditor";
import YooptaContentEditor from "../components/documents/generated/YooptaContentEditor";
import {
  generatePDFFromContent,
  generatePDFFromPreview,
  generatePDFFromPreviewElement,
} from "../utils/pdfGenerator";
import { generateDOCX } from "../utils/docxGenerator";
import { DocumentSharingModal } from "../components/sharing/DocumentSharingModal";
import { getUserDocumentPermission } from "../services/documentSharingService";
import { useUser } from "../../../foundation/altzero_base/base/contextapi/UserContext";
import { BrandingService } from "../../../foundation/altzero_base/base/services/brandingService";
import { OrganizationBranding } from "../../../foundation/altzero_base/base/types/organization";
import { supabase } from "../../../foundation/altzero_base/base/supabase/supabaseClient";
import { convertMarkdownToHTML } from "../utils/markdownUtils";
import { convertContentToBlocks } from "../utils/documentUtils";
import { generateEnhancedDOCX } from "../utils/docxGenerator";
import { SimpleExportService } from "../utils/simpleExportService";
import { saveDocument } from "../services/documentService";

// Sortable Section Component
interface SortableSectionProps {
  section: any;
  selectedSectionId: string | null;
  editingTitles: Set<string>;
  titleInputs: Record<string, string>;
  onSectionClick: (sectionId: string) => void;
  onTitleChange: (sectionId: string, value: string) => void;
  onTitleSave: (sectionId: string) => void;
  onTitleCancel: (sectionId: string) => void;
  onEditClick: (sectionId: string) => void;
  onToggleTitleEditing: (sectionId: string) => void;
  onAddSectionAfter: (sectionId: string) => void;
  onSectionRegenerate: (sectionId: string, sectionTitle: string) => void;
  onDeleteSection: (sectionId: string) => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({
  section,
  selectedSectionId,
  editingTitles,
  titleInputs,
  onSectionClick,
  onTitleChange,
  onTitleSave,
  onTitleCancel,
  onEditClick,
  onToggleTitleEditing,
  onAddSectionAfter,
  onSectionRegenerate,
  onDeleteSection,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isSelected = selectedSectionId === section.id;
  const isEditingTitle = editingTitles.has(section.id);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative border rounded-lg p-3 mb-2 cursor-pointer transition-all ${
        isSelected
          ? "border-blue-500 bg-blue-50 shadow-md"
          : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
      }`}
      onClick={() => onSectionClick(section.id)}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4 text-gray-400" />
      </div>

      <div className="ml-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-2">
            {isEditingTitle ? (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={titleInputs[section.id] || ""}
                  onChange={(e) => {
                    // Limit title length to prevent menu breaking
                    const value = e.target.value.substring(0, 100);
                    onTitleChange(section.id, value);
                  }}
                  className="text-sm font-medium"
                  placeholder="Section title (max 100 chars)"
                  maxLength={100}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onTitleSave(section.id);
                    } else if (e.key === "Escape") {
                      onTitleCancel(section.id);
                    }
                  }}
                  autoFocus
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onTitleSave(section.id)}
                  className="h-6 w-6 p-0"
                >
                  <CheckCircle className="h-3 w-3 text-green-600" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onTitleCancel(section.id)}
                  className="h-6 w-6 p-0"
                >
                  <X className="h-3 w-3 text-red-600" />
                </Button>
              </div>
            ) : (
              <h4 className="text-sm font-medium text-gray-900 truncate">
                {(section.title || "Untitled").trim()}
              </h4>
            )}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Edit Content Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(section.id);
              }}
            >
              <Edit3 className="h-3 w-3" />
            </Button>

            {/* Regenerate Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onSectionRegenerate(section.id, section.title);
              }}
            >
              <Wand2 className="h-3 w-3" />
            </Button>

            {/* More Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 max-w-xs">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleTitleEditing(section.id);
                  }}
                  className="truncate"
                >
                  <Edit3 className="h-3 w-3 mr-2 flex-shrink-0" />
                  <span className="truncate">Edit Title</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddSectionAfter(section.id);
                  }}
                  className="truncate"
                >
                  <Plus className="h-3 w-3 mr-2 flex-shrink-0" />
                  <span className="truncate">Add After</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onSectionRegenerate(section.id, section.title);
                  }}
                  className="truncate"
                >
                  <Wand2 className="h-3 w-3 mr-2 flex-shrink-0" />
                  <span className="truncate">Regenerate</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSection(section.id);
                  }}
                  className="text-red-600 truncate"
                >
                  <Trash2 className="h-3 w-3 mr-2 flex-shrink-0" />
                  <span className="truncate">Delete</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Section Preview */}
        <div className="text-xs text-gray-500 mt-1 truncate max-w-full">
          {section.content ? section.content.trim().substring(0, 60) + "..." : "No content"}
        </div>

        {/* Status Indicators */}
        <div className="flex items-center gap-2 mt-2">
          {section.regenerated && (
            <Badge variant="secondary" className="text-xs">
              ✓ Regenerated
            </Badge>
          )}
          {section.fallback && (
            <Badge variant="outline" className="text-xs">
              ⚠ Fallback
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

// Main document component wrapped in Suspense
function DocumentContent() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useUser();

  // Use the document context
  const {
    documentData,
    editableSections,
    setEditableSections,
    documentTheme,
    isLoading,
    isSaving,
    isEdited,
    documentDbId,
    clientId,
    filePath,
    pages,
    currentPage,
    setCurrentPage,
    regeneratingSection,
    handleUpdateSectionTitle,
    handleUpdateBlock,
    handleUpdateSectionContent,
    handleAddBlock,
    handleDeleteBlock,
    handleRegenerateWithAI,
    saveDocumentToSupabase,
    applyThemePreset,
    saveDocumentTheme,
    setDocumentTheme,
    saveDocumentStateToSession,
  } = useDocumentContext();

  // 🚨 ENHANCED: State variables for reliable auto-save tracking and navigation detection
  const [hasGenerationCompleted, setHasGenerationCompleted] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [autoSaveAttempted, setAutoSaveAttempted] = useState(false);
  const [isFromProposalsPage, setIsFromProposalsPage] = useState(false);

  // 🚨 ENHANCED: Detect navigation source and existing documents to prevent auto-save
  useEffect(() => {
    const documentIdFromURL = searchParams.get("id");
    const referrer = document.referrer;

    // Check if this is an existing document that shouldn't be auto-saved
    const checkExistingDocument = () => {
      // Check session storage for existing document indicators
      const existingDocData = sessionStorage.getItem(`document_${documentIdFromURL}`);
      const generatedDocData = sessionStorage.getItem("scopingai_generated_document");

      let isExistingDocument = false;

      // Check if document exists in session storage with documentDbId
      if (existingDocData) {
        try {
          const parsed = JSON.parse(existingDocData);
          if (parsed.documentDbId || parsed.isExistingDocument) {
            isExistingDocument = true;
            console.log("🔍 Found existing document in session storage:", parsed.documentDbId);
          }
        } catch (error) {
          console.error("Error parsing existing document data:", error);
        }
      }

      // Check generated document data
      if (generatedDocData) {
        try {
          const parsed = JSON.parse(generatedDocData);
          if (parsed.documentDbId || parsed.isAutoSaved) {
            isExistingDocument = true;
            console.log("🔍 Found auto-saved document in session storage:", parsed.documentDbId);
          }
        } catch (error) {
          console.error("Error parsing generated document data:", error);
        }
      }

      return isExistingDocument;
    };

    // Check various conditions for existing documents
    if (documentIdFromURL && (
      referrer.includes('/proposals') ||
      documentDbId ||
      checkExistingDocument()
    )) {
      console.log("🔍 Detected existing document - preventing auto-save:", {
        documentIdFromURL,
        fromProposals: referrer.includes('/proposals'),
        hasDocumentDbId: !!documentDbId,
        existingInSession: checkExistingDocument()
      });
      setIsFromProposalsPage(true);
      setAutoSaveAttempted(true); // Prevent auto-save for existing documents
    }
  }, [searchParams, documentDbId]);

  // 🚨 ENHANCED: Detect when a new document is loaded and needs auto-save (with debouncing)
  useEffect(() => {
    // Add a small delay to prevent multiple rapid triggers
    const timeoutId = setTimeout(() => {
      // Additional check for existing documents to prevent auto-save on refresh
      const documentIdFromURL = searchParams.get("id");
      const isExistingDocumentFromSession = () => {
        if (!documentIdFromURL) return false;

        // Check various session storage keys for existing document indicators
        const keys = [
          `document_${documentIdFromURL}`,
          "scopingai_generated_document",
          "scopingai_proposal_data"
        ];

        for (const key of keys) {
          const data = sessionStorage.getItem(key);
          if (data) {
            try {
              const parsed = JSON.parse(data);
              if (parsed.documentDbId || parsed.isAutoSaved || parsed.isExistingDocument) {
                console.log(`🔍 Found existing document indicator in ${key}:`, parsed.documentDbId || 'auto-saved');
                return true;
              }
            } catch (error) {
              // Ignore parsing errors
            }
          }
        }
        return false;
      };

      // Check if this is a new document that needs auto-save
      // Conditions: document loaded, has content, no documentDbId, not from proposals page, not existing document
      if (
        !isLoading &&
        documentData.title &&
        editableSections.length > 0 &&
        !documentDbId &&
        !isFromProposalsPage &&
        !autoSaveAttempted &&
        !isAutoSaving &&
        !isExistingDocumentFromSession()
      ) {
        console.log("🔍 Detected new document that needs auto-save:", {
          title: documentData.title,
          sectionsCount: editableSections.length,
          documentDbId,
          isFromProposalsPage,
          autoSaveAttempted,
          isLoading,
          isAutoSaving,
          isExistingFromSession: isExistingDocumentFromSession()
        });

        // Set generation completed to trigger auto-save
        setHasGenerationCompleted(true);
        console.log("✅ Set hasGenerationCompleted to true - auto-save should trigger");
      } else {
        console.log("🔍 Auto-save conditions not met:", {
          isLoading,
          hasTitle: !!documentData.title,
          sectionsCount: editableSections.length,
          documentDbId,
          isFromProposalsPage,
          autoSaveAttempted,
          isAutoSaving,
          isExistingFromSession: isExistingDocumentFromSession()
        });
      }
    }, 500); // 500ms delay to prevent rapid triggers

    return () => clearTimeout(timeoutId);
  }, [isLoading, documentData.title, editableSections.length, documentDbId, isFromProposalsPage, autoSaveAttempted, isAutoSaving, searchParams]);

  // Debug logging for pages and sections
  useEffect(() => {
    console.log("🔍 DocumentsGenerated: Component state changed");
    console.log("- isLoading:", isLoading);
    console.log("- pages length:", pages?.length || 0);
    console.log("- editableSections length:", editableSections?.length || 0);
    console.log("- currentPage:", currentPage);

    // 🚨 CRITICAL DEBUG: Check if all sections are in pages
    if (pages && pages.length > 0) {
      const allSectionsInPages = pages.flatMap(page => page.content || []);
      console.log("🔍 All sections in pages:", allSectionsInPages.length);
      console.log("🔍 Section IDs in pages:", allSectionsInPages.map(s => s.id));
      console.log("🔍 Section IDs in editableSections:", editableSections?.map(s => s.id) || []);

      // Check if any sections are missing from pages
      const missingSections = editableSections?.filter(section =>
        !allSectionsInPages.some(pageSection => pageSection.id === section.id)
      ) || [];

      if (missingSections.length > 0) {
        console.error("🚨 MISSING SECTIONS IN PAGES:", missingSections.map(s => ({ id: s.id, title: s.title })));
      } else {
        console.log("✅ All sections are present in pages");
      }
    }

    console.log("- pages:", pages);
    console.log("- editableSections:", editableSections);

    // Emergency fix: If loading is done but pages is empty, try to create pages from sections
    if (
      !isLoading &&
      (!pages || pages.length === 0) &&
      editableSections &&
      editableSections.length > 0
    ) {
      console.warn(
        "EMERGENCY FIX: Pages is empty but we have sections, manually triggering page creation"
      );
      // Import the splitIntoPages function
      import("../utils/documentUtils").then(({ splitIntoPages }) => {
        const newPages = splitIntoPages(editableSections);
        console.log("Emergency created pages:", newPages);
        // We can't directly call setPages here since it's from context, but this will help debug
      });
    }
  }, [isLoading, pages, editableSections]);

  // 🚨 PHASE 6: Save document state to sessionStorage when edited
  useEffect(() => {
    if (isEdited && !isLoading && documentDbId) {
      console.log("🔄 Document edited - saving state to sessionStorage");
      saveDocumentStateToSession();
    }
  }, [isEdited, isLoading, documentDbId, saveDocumentStateToSession]);

  // 🚨 ENHANCED: Reliable auto-save trigger using useEffect
  useEffect(() => {
    const performAutoSave = async () => {
      // Only auto-save if:
      // 1. Generation has completed OR document is loaded with content
      // 2. This is a new document (no documentDbId)
      // 3. Document has a title and sections
      // 4. Auto-save hasn't been attempted yet
      // 5. Not currently auto-saving
      // 6. Not from proposals page (existing document)
      // 7. Not currently saving manually
      if (
        hasGenerationCompleted &&
        !documentDbId &&
        documentData.title.trim() &&
        editableSections.length > 0 &&
        !autoSaveAttempted &&
        !isAutoSaving &&
        !isFromProposalsPage &&
        !isSaving
      ) {
        console.log("🔄 Starting auto-save after generation completion...");
        console.log("🔄 Auto-save conditions met:", {
          hasGenerationCompleted,
          documentDbId,
          title: documentData.title,
          sectionsCount: editableSections.length,
          autoSaveAttempted,
          isAutoSaving,
          isFromProposalsPage
        });
        setIsAutoSaving(true);
        setAutoSaveAttempted(true);

        try {
          console.log("💾 Auto-saving new document after generation...");

          // 🚨 NEW: Set auto-save flag to prevent toast notifications
          sessionStorage.setItem('scopingai_auto_save_in_progress', 'true');

          // Wait for save to complete and get the result
          const saveResult = await saveDocumentToSupabase();
          console.log("💾 Auto-save result:", saveResult);

          // 🚨 NEW: Check if save was successful
          if (!saveResult?.success) {
            throw new Error(saveResult?.error || 'Save operation failed');
          }

          // 🚨 ENHANCED: Wait for context to update with retry logic
          let retryCount = 0;
          const maxRetries = 10;

          while (!documentDbId && retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 200));
            retryCount++;
            console.log(`⏳ Waiting for documentDbId to be available (attempt ${retryCount}/${maxRetries})`);
          }

          // Check if documentDbId is now available from context
          if (documentDbId) {
            console.log("✅ Context documentDbId is now available:", documentDbId);

            // 🚨 ENHANCED: Create comprehensive session data
            const sessionData = {
              documentData,
              editableSections,
              documentTheme,
              documentDbId,
              clientId,
              isAutoSaved: true,
              isNewDocument: false,
              isExistingDocument: true,
              timestamp: Date.now(),
            };

            // Store under the generated document key
            sessionStorage.setItem("scopingai_generated_document", JSON.stringify(sessionData));
            console.log("✅ Updated sessionStorage with documentDbId after auto-save:", documentDbId);

            // Call the context method to save complete state
            saveDocumentStateToSession();

            console.log("✅ Auto-save completed successfully");

            // Show success message
            toast({
              title: "✅ Document Auto-Saved",
              description: "Your proposal has been automatically saved to the database.",
              duration: 3000,
            });

            // 🚨 FIXED: Reset hasGenerationCompleted to prevent re-triggering
            setHasGenerationCompleted(false);

          } else {
            console.warn("⚠️ documentDbId still not available after retries, but auto-save completed");

            // Still show success message as the save operation completed
            toast({
              title: "✅ Document Auto-Saved",
              description: "Your proposal has been automatically saved to the database.",
              duration: 3000,
            });

            // 🚨 FIXED: Don't refresh page - just ensure UI updates properly
            console.log("✅ Auto-save completed successfully (documentDbId will be available soon)");
          }
        } catch (error) {
          console.error("❌ Auto-save failed:", error);
          setAutoSaveAttempted(false); // Allow retry on next attempt

          // Clear the auto-save flag on error
          sessionStorage.removeItem('scopingai_auto_save_in_progress');

          toast({
            title: "Auto-Save Failed",
            description: "Document generated successfully but couldn't be saved automatically. Please save manually.",
            variant: "destructive",
          });
        } finally {
          setIsAutoSaving(false);
        }
      }
    };

    performAutoSave();
  }, [hasGenerationCompleted, documentData.title, editableSections.length, documentDbId, autoSaveAttempted, isAutoSaving, isFromProposalsPage, isSaving, saveDocumentToSupabase, saveDocumentStateToSession, toast]);

  // 🚨 ENHANCED: Handle documentDbId availability and prevent further auto-save
  useEffect(() => {
    if (documentDbId) {
      console.log("🔄 documentDbId is now available:", documentDbId);

      // If documentDbId exists, this is an existing document - prevent auto-save
      if (!autoSaveAttempted) {
        console.log("🔍 DocumentDbId available - marking as existing document to prevent auto-save");
        setAutoSaveAttempted(true);
        setIsFromProposalsPage(true);
      }

      // Update session storage if auto-save was attempted
      if (autoSaveAttempted && !isAutoSaving) {
        console.log("🔄 Updating session storage after auto-save completion");

        // Ensure sessionStorage is updated with the correct documentDbId
        const sessionData = sessionStorage.getItem("scopingai_generated_document");
        if (sessionData) {
          const parsedData = JSON.parse(sessionData);
          if (parsedData.documentDbId !== documentDbId) {
            parsedData.documentDbId = documentDbId;
            parsedData.isAutoSaved = true;
            parsedData.isNewDocument = false;
            parsedData.isExistingDocument = true;

            sessionStorage.setItem("scopingai_generated_document", JSON.stringify(parsedData));
            console.log("✅ Updated sessionStorage with correct documentDbId:", documentDbId);
          }
        }

        // Call context method to ensure complete state synchronization
        saveDocumentStateToSession();

        // 🚨 NEW: Force a component re-render to update button text
        setPreviewKey(prev => prev + 1);
        console.log("🔄 Forced UI refresh to show Update button");
      }
    }
  }, [documentDbId, autoSaveAttempted, isAutoSaving, saveDocumentStateToSession]);

  // Permission state
  const [userPermission, setUserPermission] = useState<string | null>(null);
  const [permissionLoading, setPermissionLoading] = useState(true);

  // Organization branding state (must be declared before use in fetchOrganizationBranding)
  const [organizationBranding, setOrganizationBranding] =
    useState<OrganizationBranding | null>(null);
  const [isLoadingBranding, setIsLoadingBranding] = useState(false);

  // Document-specific branding overrides (temporary, not saved to organization_branding)
  const [documentBrandingOverrides, setDocumentBrandingOverrides] = useState({
    primary_color: "",
    secondary_color: "",
    accent_color: "",
    font_color: "",
    font_family: "",
  });

  // Section management state
  const [editingTitles, setEditingTitles] = useState<Set<string>>(new Set());
  const [titleInputs, setTitleInputs] = useState<Record<string, string>>({});

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Section management handlers
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = editableSections.findIndex((section) => section.id === active.id);
      const newIndex = editableSections.findIndex((section) => section.id === over.id);
      const reorderedSections = arrayMove(editableSections, oldIndex, newIndex);
      setEditableSections(reorderedSections);

      toast({
        title: "Section Reordered",
        description: "Section order has been updated",
        duration: 2000,
      });
    }
  };

  const handleTitleEdit = (sectionId: string) => {
    const section = editableSections.find(s => s.id === sectionId);
    if (section) {
      setEditingTitles(prev => new Set([...prev, sectionId]));
      setTitleInputs(prev => ({ ...prev, [sectionId]: section.title }));
    }
  };

  const handleTitleSave = (sectionId: string) => {
    const newTitle = titleInputs[sectionId];
    if (newTitle && newTitle.trim()) {
      // Trim and limit title length to prevent menu breaking
      const trimmedTitle = newTitle.trim().substring(0, 100); // Max 100 characters
      handleUpdateSectionTitle(sectionId, trimmedTitle);
      setEditingTitles(prev => {
        const newSet = new Set(prev);
        newSet.delete(sectionId);
        return newSet;
      });
      setTitleInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[sectionId];
        return newInputs;
      });
    }
  };

  const handleTitleCancel = (sectionId: string) => {
    setEditingTitles(prev => {
      const newSet = new Set(prev);
      newSet.delete(sectionId);
      return newSet;
    });
    setTitleInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[sectionId];
      return newInputs;
    });
  };

  const handleAddSectionAfter = (afterSectionId: string) => {
    const afterSectionIndex = editableSections.findIndex(s => s.id === afterSectionId);
    if (afterSectionIndex === -1) return;

    const newSectionId = `section-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newSection = {
      id: newSectionId,
      title: "New Section".trim(),
      content: "",
      blocks: [],
      status: "draft" as const,
      level: 1,
      order: afterSectionIndex + 1,
    };

    setEditableSections(prev => {
      const updatedSections = [...prev];
      updatedSections.splice(afterSectionIndex + 1, 0, newSection);

      // Update order for sections after the insertion point
      updatedSections.forEach((section, index) => {
        if (index > afterSectionIndex) {
          section.order = index;
        }
      });

      return updatedSections;
    });

    setSelectedSectionId(newSectionId);

    toast({
      title: "Section Added",
      description: "New section added successfully",
      duration: 2000,
    });
  };

  const handleDeleteSection = (sectionId: string) => {
    const section = editableSections.find(s => s.id === sectionId);
    if (editableSections.length <= 1) {
      toast({
        title: "Cannot Delete",
        description: "Cannot delete the only section. At least one section must remain.",
        variant: "destructive",
      });
      return;
    }

    if (confirm(`Are you sure you want to delete "${section?.title}"?`)) {
      setEditableSections(prev => prev.filter(s => s.id !== sectionId));

      // If deleted section was selected, select the first remaining section
      if (selectedSectionId === sectionId) {
        const remainingSections = editableSections.filter(s => s.id !== sectionId);
        setSelectedSectionId(remainingSections.length > 0 ? remainingSections[0].id : null);
      }

      toast({
        title: "Section Deleted",
        description: `"${section?.title}" has been removed.`,
      });
    }
  };

  // 🚨 NEW: Project title editing state
  const [isEditingProjectTitle, setIsEditingProjectTitle] = useState(false);
  const [projectTitleInput, setProjectTitleInput] = useState("");

  // 🚨 NEW: Regeneration state (removed method popup - go directly to AI prompt)
  const [pendingRegenerationType, setPendingRegenerationType] = useState<'full' | 'section' | null>(null);
  const [pendingSectionId, setPendingSectionId] = useState<string | null>(null);
  const [pendingSectionTitle, setPendingSectionTitle] = useState<string | null>(null);
  const [regenerationProgress, setRegenerationProgress] = useState({
    isGenerating: false,
    progress: 0,
    currentStep: "",
    message: "",
    completedSections: [] as string[],
  });

  // 🚨 NEW: AI Prompt state (same as regeneration page)
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [isRegeneratingWithPrompt, setIsRegeneratingWithPrompt] = useState(false);

  // Force editor remount when content updates
  const [editorMountKey, setEditorMountKey] = useState(0);

  // 🚨 NEW: Track selected section to preserve after regeneration
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);

  // 🚨 NEW: Save debouncing state
  const [isSavingDebounced, setIsSavingDebounced] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const saveCountRef = useRef(0);

  // 🚨 NEW: Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const hasUnsavedChangesRef = useRef(false);

  // Helper function to update both state and ref
  const updateHasUnsavedChanges = (value: boolean) => {
    const timestamp = new Date().toISOString();
    console.log(`🚨 [${timestamp}] updateHasUnsavedChanges called with:`, value);
    hasUnsavedChangesRef.current = value;
    setHasUnsavedChanges(value);
  };



  // 🚨 NEW: Debounced save function to prevent duplicates
  const debouncedSave = useCallback(async (saveFunction: () => Promise<any>) => {
    if (isSavingDebounced) {
      console.log("🚫 Save already in progress, skipping duplicate save request");
      return;
    }

    // Increment save counter for tracking
    saveCountRef.current += 1;
    const currentSaveId = saveCountRef.current;
    console.log(`💾 Save request #${currentSaveId} initiated`);

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set debouncing state
    setIsSavingDebounced(true);

    // Debounce the save operation
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        console.log(`💾 Executing save request #${currentSaveId}`);
        await saveFunction();
        console.log(`✅ Save request #${currentSaveId} completed successfully`);

        // Clear unsaved changes flag on successful save
        updateHasUnsavedChanges(false);

        // 🚨 FIXED: Force preview refresh after successful save
        setPreviewKey(prev => prev + 1);
      } catch (error) {
        console.error(`❌ Save request #${currentSaveId} failed:`, error);
        throw error;
      } finally {
        setIsSavingDebounced(false);
      }
    }, 500); // 500ms debounce delay
  }, [isSavingDebounced]);

  // 🚨 NEW: Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Force preview update when sections change
  useEffect(() => {
    setPreviewKey(prev => prev + 1);
  }, [editableSections]);

  // 🚨 FIXED: Regenerate All inline (no redirect)
  const handleRegenerateAll = () => {
    console.log("🔄 Starting full document regeneration inline (no redirect)");

    // 🚨 FIXED: Ensure clean state before opening dialog
    if (showPromptDialog) {
      console.log("🚫 Dialog already open, ignoring duplicate request");
      return;
    }

    // Reset any existing state
    setCustomPrompt("");
    setIsRegeneratingWithPrompt(false);

    // Set new state
    setPendingRegenerationType('full');
    setPendingSectionId(null);
    setPendingSectionTitle(null);

    // 🚨 NEW: Preserve current selected section for "Regenerate All"
    // If no section is currently selected, keep the first section selected
    if (!selectedSectionId && editableSections.length > 0) {
      setSelectedSectionId(editableSections[0].id);
    }

    // Small delay to ensure state is set
    setTimeout(() => {
      setShowPromptDialog(true);
    }, 50);
  };

  const handleRegenerateSection = (sectionId: string, sectionTitle: string) => {
    console.log("🔄 Starting section regeneration from edit page:", { sectionId, sectionTitle });

    // 🚨 FIXED: Ensure clean state before opening dialog
    if (showPromptDialog) {
      console.log("🚫 Dialog already open, ignoring duplicate request");
      return;
    }

    // Reset any existing state
    setCustomPrompt("");
    setIsRegeneratingWithPrompt(false);

    // Set new state
    setPendingRegenerationType('section');
    setPendingSectionId(sectionId);
    setPendingSectionTitle(sectionTitle);

    // 🚨 NEW: Remember which section we're regenerating
    setSelectedSectionId(sectionId);

    // Small delay to ensure state is set
    setTimeout(() => {
      setShowPromptDialog(true);
    }, 50);
  };

  // 🚨 REMOVED: No longer needed since we go directly to AI prompt dialog

  // 🚨 FIXED: AI Prompt regeneration function (handles both full and section)
  const handleRegenerateWithPrompt = async () => {
    if (!customPrompt.trim()) {
      toast({
        title: "Prompt Required",
        description: "Please enter a custom prompt for regeneration.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsRegeneratingWithPrompt(true);

      // 🚨 FIXED: Don't close dialog immediately - keep it open to show progress
      console.log("🚀 Starting regeneration, keeping dialog open for progress...");

      const isFullRegeneration = pendingRegenerationType === 'full';
      const isSectionRegeneration = pendingRegenerationType === 'section';

      console.log("🚀 Starting AI prompt regeneration from edit page...", {
        type: pendingRegenerationType,
        sectionId: pendingSectionId,
        sectionTitle: pendingSectionTitle,
        isFullRegeneration,
        isSectionRegeneration
      });

      setRegenerationProgress({
        isGenerating: true,
        progress: 0,
        currentStep: isFullRegeneration ? "Regenerating all sections..." : `Regenerating "${pendingSectionTitle}"...`,
        message: "Setting up AI prompt regeneration",
        completedSections: [],
      });

      // 🚨 FIXED: Use correct API endpoints like DocumentsRegeneration
      let response;

      if (pendingRegenerationType === 'section' && pendingSectionId) {
        // Single section regeneration - use section-specific endpoint
        const sectionToRegenerate = editableSections.find(s => s.id === pendingSectionId);

        console.log("🔄 Single section regeneration:", {
          sectionId: pendingSectionId,
          sectionTitle: pendingSectionTitle,
          currentContent: sectionToRegenerate?.content?.length || 0
        });

        response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/tender/regenerate-section-with-prompt/stream`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": "scopingai",
              "x-user-id": user?.id || "",
            },
            body: JSON.stringify({
              documentId: documentDbId || searchParams.get("id") || "",
              sectionId: pendingSectionId,
              sectionTitle: pendingSectionTitle,
              currentContent: sectionToRegenerate?.content || "",
              customPrompt: customPrompt.trim(),
              preserveImages: true,
            }),
          }
        );
      } else {
        // Full document regeneration - use full document endpoint
        console.log("🔄 Full document regeneration:", {
          totalSections: editableSections.length,
          customPrompt: customPrompt.trim()
        });

        response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/tender/regenerate-with-prompt/stream`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": "scopingai",
              "x-user-id": user?.id || "",
            },
            body: JSON.stringify({
              documentId: documentDbId || searchParams.get("id") || "",
              sections: editableSections,
              customPrompt: customPrompt.trim(),
              preserveImages: true,
            }),
          }
        );
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ API call failed:", {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText
        });
        throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      console.log("✅ API call successful, starting SSE stream...");

      // Handle Server-Sent Events for real-time progress
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log("🔄 SSE Data received:", data);

                if (data.type === 'progress') {
                  setRegenerationProgress(prev => ({
                    ...prev,
                    progress: data.progress || 0,
                    currentStep: data.step || prev.currentStep,
                    message: data.message || prev.message,
                  }));
                } else if (data.status === 'completed') {
                  // 🚨 FIXED: Handle different response formats based on API endpoint
                  console.log("✅ Regeneration completed:", data);

                  if (pendingRegenerationType === 'section' && pendingSectionId) {
                    // 🚨 FIX: Single section regeneration - API returns data.result (consistent format)
                    if (data.result && data.result.content) {
                      console.log("🔄 Updating single section:", data.result.section_id);
                      console.log("🔄 New content length:", data.result.content.length);

                      setEditableSections(prev => prev.map(section =>
                        section.id === pendingSectionId // Use pendingSectionId instead of data.result.section_id
                          ? {
                              ...section,
                              content: data.result.content,
                              regenerated: true,
                              regeneratedAt: new Date().toISOString(),
                            }
                          : section
                      ));

                      // 🚨 FIX: Maintain focus on regenerated section with delay
                      console.log("🎯 Maintaining focus on regenerated section:", pendingSectionId);
                      setSelectedSectionId(pendingSectionId);

                      // Double-check focus after state update
                      setTimeout(() => {
                        console.log("🎯 Double-checking focus on section:", pendingSectionId);
                        setSelectedSectionId(pendingSectionId);
                      }, 100);
                    }
                  } else if (data.sections && Array.isArray(data.sections)) {
                    // Full document regeneration - API returns data.sections
                    console.log("🔄 Updating all sections:", data.sections.length);
                    setEditableSections([...data.sections]);

                    // 🚨 FIX: Maintain focus on selected section after full regeneration
                    if (selectedSectionId) {
                      console.log("🎯 Maintaining focus after full regeneration:", selectedSectionId);
                      setTimeout(() => {
                        setSelectedSectionId(selectedSectionId);
                      }, 100);
                    }
                  }

                  // 🚨 SIMPLE UI REFRESH
                  console.log("🔄 Forcing UI refresh...");
                  setEditorMountKey(prev => prev + 1);
                  setPreviewKey(prev => prev + 1);

                } else if (data.type === 'complete' || data.progress === 100) {
                  console.log("🎉 Regeneration complete!");

                  setRegenerationProgress(prev => ({
                    ...prev,
                    progress: 100,
                    currentStep: "Complete!",
                    message: "All sections regenerated successfully",
                    isGenerating: false,
                  }));

                  // 🚨 FIXED: Force final UI update
                  setPreviewKey(prev => prev + 1);

                  // Mark as having unsaved changes
                  const timestamp3 = new Date().toISOString();
                  console.log(`🚨 [${timestamp3}] SETTING hasUnsavedChanges to TRUE (regeneration complete)`);
                  updateHasUnsavedChanges(true);

                  const successMessage = pendingRegenerationType === 'full'
                    ? "All sections regenerated successfully with your custom prompt!"
                    : `Section "${pendingSectionTitle}" regenerated successfully with your custom prompt!`;

                  toast({
                    title: "Success!",
                    description: successMessage,
                    duration: 5000,
                  });

                  // Close dialog after success
                  setTimeout(() => {
                    setShowPromptDialog(false);
                  }, 1000);

                  break;
                }
              } catch (parseError) {
                console.warn("Failed to parse prompt regeneration SSE data:", parseError);
              }
            }
          }
        }
      }

    } catch (error) {
      console.error("❌ Prompt regeneration failed:", error);
      toast({
        title: "Regeneration Failed",
        description: `Failed to regenerate with prompt: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsRegeneratingWithPrompt(false);
      setCustomPrompt("");
      setRegenerationProgress(prev => ({
        ...prev,
        isGenerating: false,
      }));

      // 🚨 FIXED: Close dialog and reset pending state
      setShowPromptDialog(false);
      setPendingRegenerationType(null);
      setPendingSectionId(null);
      setPendingSectionTitle(null);
    }
  };

  // Fetch organization branding data
  const fetchOrganizationBranding = async () => {
    if (!user?.id) {
      console.log("❌ No user ID available for branding fetch");
      return;
    }

    setIsLoadingBranding(true);
    console.log("🔄 Fetching organization branding for user:", user.id);

    try {
      // Get user's organization ID
      const { data: orgMember, error: orgError } = await supabase
        .from("organisation_members")
        .select("organisation_id")
        .eq("user_id", user.id)
        .single();

      if (orgError) {
        console.log("⚠️ No organization membership found:", orgError.message);
        return;
      }

      if (!orgMember?.organisation_id) {
        console.log("⚠️ No organization ID found for user");
        return;
      }

      console.log("🏢 Found organization ID:", orgMember.organisation_id);

      // Get organization branding
      const { data: branding, error: brandingError } = await supabase
        .from("organisation_branding")
        .select("*")
        .eq("organisation_id", orgMember.organisation_id)
        .single();

      if (brandingError) {
        console.log(
          "⚠️ No organization branding found:",
          brandingError.message
        );
        return;
      }

      if (branding) {
        console.log("✅ Organization branding loaded successfully:", branding);
        setOrganizationBranding(branding);
      } else {
        console.log("⚠️ No branding data returned");
      }
    } catch (error) {
      console.error("❌ Error fetching organization branding:", error);
    } finally {
      setIsLoadingBranding(false);
      console.log("🔄 Branding fetch completed");
    }
  };

  // Load branding when component mounts
  useEffect(() => {
    if (user) {
      fetchOrganizationBranding();
    }
  }, [user]);

  // Initialize documentTheme with organization branding when organization branding loads
  useEffect(() => {
    if (
      organizationBranding &&
      !isLoadingBranding &&
      organizationBranding.primary_color
    ) {
      console.log(
        "🎨 Initializing documentTheme with organization branding:",
        organizationBranding
      );

      // Only update theme if it doesn't already have organization colors applied
      const currentTheme = documentTheme;
      const hasOrgColors =
        currentTheme &&
        (currentTheme.headingColor === organizationBranding.primary_color ||
          currentTheme.textColor === organizationBranding.secondary_color ||
          currentTheme.accentColor === organizationBranding.accent_color);

      if (!hasOrgColors) {
        // Create enhanced theme with hierarchical organization branding
        const enhancedTheme = {
          // Start with existing theme, then apply organization branding
          ...currentTheme,
          // Apply hierarchical color system from organization branding
          headingColor:
            organizationBranding.primary_color ||
            currentTheme?.headingColor ||
            "#1a1a1a", // Primary color for H1 headings
          subheadingColor:
            organizationBranding.secondary_color ||
            currentTheme?.subheadingColor ||
            "#374151", // Secondary color for H2, H3 headings
          textColor:
            organizationBranding.font_color ||
            organizationBranding.secondary_color ||
            currentTheme?.textColor ||
            "#333333", // Font color for body text
          accentColor:
            organizationBranding.accent_color ||
            organizationBranding.primary_color ||
            currentTheme?.accentColor ||
            "#0066cc", // Accent color for highlights
          fontFamily:
            organizationBranding.font_family ||
            currentTheme?.fontFamily ||
            "Inter, system-ui, -apple-system, sans-serif",
          backgroundColor: "#ffffff",
          // Organization-specific branding info
          brandName:
            organizationBranding.brand_title || currentTheme?.brandName,
          brandTagline:
            organizationBranding.tagline || currentTheme?.brandTagline,
          // Organization logo
          logo: organizationBranding.logo_url
            ? BrandingService.getLogoUrlWithCacheBusting(
                organizationBranding.logo_url
              )
            : currentTheme?.logo,
        };

        console.log(
          "🔄 Updating documentTheme with organization defaults:",
          enhancedTheme
        );
        setDocumentTheme(enhancedTheme);
      }
    }
  }, [
    organizationBranding,
    isLoadingBranding,
    documentTheme,
    setDocumentTheme,
  ]);

  // Check user permissions for this document
  useEffect(() => {
    const checkPermissions = async () => {
      if (!user || !documentDbId) {
        setPermissionLoading(false);
        return;
      }

      try {
        const permission = await getUserDocumentPermission(
          documentDbId,
          user.id
        );
        setUserPermission(permission);
      } catch (error) {
        console.error("Error checking permissions:", error);
        setUserPermission(null);
      } finally {
        setPermissionLoading(false);
      }
    };

    checkPermissions();
  }, [user, documentDbId]);

  // Check if user can edit this document
  const canEdit =
    userPermission === null ||
    userPermission === "admin" ||
    userPermission === "edit";
  const isViewOnly = userPermission === "view";

  // Local UI state - Enhanced with new features
  const [activeTab, setActiveTab] = useState("preview");
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingDOCX, setIsGeneratingDOCX] = useState(false);
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState<
    "theme" | "branding"
  >("theme");
  // Removed edit mode states - editing now only in content editor tab




  
  // Key for forcing preview re-render when sections change
  const [previewKey, setPreviewKey] = useState(0);

  // Sharing modal state
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);

  // Live generation state
  const [isLiveGenerating, setIsLiveGenerating] = useState(false);
  const [liveGenerationProgress, setLiveGenerationProgress] = useState(0);
  const [liveGenerationStatus, setLiveGenerationStatus] = useState<string[]>(
    []
  );
  const [liveGeneratedSections, setLiveGeneratedSections] = useState<
    Record<string, string>
  >({});
  const eventSourceRef = useRef<EventSource | null>(null);

  // Extract URL parameters for live generation
  const clientFromURL = searchParams.get("title") || "Client";
  const projectFromURL = searchParams.get("project") || "Project";

  // Detect if this is a live generation session
  const isLiveGeneration = useMemo(() => {
    // Check URL parameters for live generation indicators
    const knowledgeUsed = searchParams.get("knowledgeUsed");
    const knowledgeCount = searchParams.get("knowledgeCount");

    // If knowledge base was used, this could be a live generation
    return (
      knowledgeUsed === "true" && knowledgeCount && parseInt(knowledgeCount) > 0
    );
  }, [searchParams]);

  // Create unified branded theme that merges organization branding + document overrides + base theme
  const unifiedBrandedTheme = useMemo(() => {
    const baseTheme: any = {
      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
      headingColor: "#1a1a1a",
      subheadingColor: "#374151",
      textColor: "#333333",
      accentColor: "#0066cc",
      backgroundColor: "#ffffff",
      headingSize: "2.25rem", // 36px
      textSize: "1rem", // 16px
      brandName: organizationBranding?.brand_title || "",
      brandTagline: organizationBranding?.tagline || "",
      logo: organizationBranding?.logo_url
        ? BrandingService.getLogoUrlWithCacheBusting(organizationBranding.logo_url)
        : "",

    };

    // Simple approach: Theme Settings is the source of truth
    // Organization branding should be baked into documentTheme at initialization
    const proposalBranding = (documentData as any)?.branding;

    const mergedTheme = {
      ...baseTheme,
      ...documentTheme,
      // Apply hierarchical color system with proposal branding and document overrides
      headingColor:
        proposalBranding?.primaryColor ||
        documentBrandingOverrides.primary_color ||
        baseTheme.headingColor, // Primary color for H1 headings
      subheadingColor:
        proposalBranding?.secondaryColor ||
        documentBrandingOverrides.secondary_color ||
        baseTheme.subheadingColor,
      textColor:
        documentBrandingOverrides.font_color ||
        proposalBranding?.secondaryColor ||
        documentBrandingOverrides.secondary_color ||
        baseTheme.textColor, // Font color for body text
      fontFamily: documentBrandingOverrides.font_family || baseTheme.fontFamily,
      backgroundColor: "#ffffff", // Always white
      // Branding info (proposal > organization > theme)
      brandTitle:
        proposalBranding?.brandTitle ||
        organizationBranding?.brand_title ||
        baseTheme?.brandName,
      tagline:
        proposalBranding?.tagline ||
        organizationBranding?.tagline ||
        baseTheme?.brandTagline,
      brandName:
        proposalBranding?.brandTitle ||
        organizationBranding?.brand_title ||
        baseTheme?.brandName,
      brandTagline:
        proposalBranding?.tagline ||
        organizationBranding?.tagline ||
        baseTheme?.brandTagline,
      logo:
        (documentData as any)?.logo?.src || organizationBranding?.logo_url
          ? BrandingService.getLogoUrlWithCacheBusting(
              organizationBranding.logo_url
            )
          : baseTheme?.logo,
      contactInfo: organizationBranding
        ? {
            website: organizationBranding.website_url,
            email: organizationBranding.contact_email,
            phone: organizationBranding.phone_number,
            address: organizationBranding.address,
          }
        : undefined,
      headingSize: baseTheme.headingSize,
      textSize: baseTheme.textSize,

    };

    return mergedTheme;
  }, [documentTheme, documentBrandingOverrides, organizationBranding, documentData]);

  const titleFromURL = searchParams?.get("title");
  const preserveOriginal = searchParams?.get("preserveOriginal") === "true";

  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const documentContainerRef = useRef<HTMLDivElement>(null);

  // Enhanced PDF download with modern export service
  // Enhanced PDF download with professional formatting
  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      toast({
        title: "Generating Professional PDF",
        description:
          "Creating your document with exact preview formatting, images, and logos...",
      });

      const filename = `${
        documentData.title?.replace(/[^a-z0-9]/gi, "_") || "document"
      }.pdf`;

      // Prepare sections with HTML content for PDF
      const sections = editableSections.map(section => ({
        ...section,
        // Convert markdown to HTML for each section
        htmlContent: convertMarkdownToHTML(section.content || "")
      }));

      // Use a PDF generator that renders HTML (prefer generatePDFFromContent if available)
      if (typeof generatePDFFromContent === 'function') {
        await generatePDFFromContent(
          { ...documentData, sections },
          unifiedBrandedTheme,
          filename
        );
      } else if (documentContainerRef.current) {
        await generatePDFFromPreviewElement(
          documentContainerRef.current,
          filename,
          unifiedBrandedTheme
        );
      } else {
        // Fallback: use generatePDFFromPreview
        await generatePDFFromPreview(
          { ...documentData, sections },
          unifiedBrandedTheme,
          filename
        );
      }

      toast({
        title: "PDF Downloaded Successfully",
        description:
          "Your professional document has been saved.",
        duration: 5000,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Download Failed",
        description: "There was an error creating your PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Enhanced DOCX download with professional formatting
  const handleDownloadDOCX = async () => {
    setIsGeneratingDOCX(true);
    try {
      toast({
        title: "Generating Professional DOCX",
        description:
          "Creating your Word document with proper markdown formatting and images...",
      });

      const filename = `${
        documentData.title?.replace(/[^a-z0-9]/gi, "_") || "document"
      }.docx`;

      // Prepare sections with blocks for DOCX
      const sections = editableSections.map(section => ({
        ...section,
        blocks: convertContentToBlocks(section.content || "")
      }));

      // Use generateEnhancedDOCX for clean formatting
      await generateEnhancedDOCX(
        { ...documentData, sections },
        unifiedBrandedTheme,
        filename
      );

      toast({
        title: "DOCX Downloaded Successfully",
        description:
          "Your professional Word document has been saved.",
        duration: 5000,
      });
    } catch (error) {
      console.error("Error generating DOCX:", error);
      toast({
        title: "Download Failed",
        description:
          "There was an error creating your DOCX file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingDOCX(false);
    }
  };

  // Reset document branding overrides
  const resetDocumentBranding = () => {
    setDocumentBrandingOverrides({
      primary_color: "",
      secondary_color: "",
      accent_color: "",
      font_color: "",
      font_family: "",
    });
  };

  // 🚨 NEW: Project title editing functions
  const toggleProjectTitleEditing = () => {
    if (isEditingProjectTitle) {
      // Cancel editing
      setIsEditingProjectTitle(false);
      setProjectTitleInput("");
    } else {
      // Start editing
      setIsEditingProjectTitle(true);
      setProjectTitleInput(documentData.title || "");
    }
  };

  const handleProjectTitleChange = (newTitle: string) => {
    setProjectTitleInput(newTitle);
  };

  const handleProjectTitleSave = async () => {
    if (projectTitleInput.trim()) {
      try {
        // 🚨 FIXED: Only update title, don't save entire document (prevents creating new proposal)
        if (documentData && documentData.title !== projectTitleInput.trim()) {
          // Update the document title in local state only
          documentData.title = projectTitleInput.trim();

          // 🚨 FIXED: Update title in database directly without creating new document
          if (documentDbId) {
            const { error } = await supabase
              .from("scopingai_documents")
              .update({
                title: projectTitleInput.trim(),
                updated_at: new Date().toISOString(),
              })
              .eq("id", documentDbId);

            if (error) {
              throw error;
            }
          }
        }

        setIsEditingProjectTitle(false);
        setProjectTitleInput("");

        toast({
          title: "Project Title Updated",
          description: "Project title has been updated successfully",
          duration: 2000,
        });
      } catch (error) {
        console.error("Error updating project title:", error);
        toast({
          title: "Error",
          description: "Failed to update project title",
          duration: 2000,
        });
      }
    }
  };

  const handleProjectTitleCancel = () => {
    setIsEditingProjectTitle(false);
    setProjectTitleInput("");
  };

  // Add function to handle image upload
  const handleImageUpload = (sectionId: string) => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
      fileInputRef.current.onchange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
          const file = target.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              if (event.target && typeof event.target.result === "string") {
                const imageUrl = event.target.result;

                // Create and add image block using context functions
                const newBlock = {
                  id: `block-${Date.now()}-${Math.random()
                    .toString(36)
                    .slice(2)}`,
                  type: "image" as const,
                  content: imageUrl,
                };

                handleAddBlock(sectionId, "image");

                toast({
                  title: "Image added",
                  description: "The image has been added to the section.",
                });
              }
            };
            reader.readAsDataURL(file);
          }
        }
      };
    }
  };

  // Enhanced section update function
  const handleSectionUpdate = (sectionId: string, updates: any) => {
    // Use the existing context functions to update sections
    if (updates.blocks) {
      // Convert blocks back to text content for compatibility
      const textContent = updates.blocks
        .map((block: any) => {
          switch (block.type) {
            case "text":
              return block.content.text || "";
            case "heading":
              return `# ${block.content.text || ""}`;
            default:
              return "";
          }
        })
        .join("\n\n");

      handleUpdateBlock(sectionId, "content", { content: textContent });
    }
  };

  // Live generation effect - Start streaming when live generation is detected
  useEffect(() => {
    // Check if there's already a generated document in session storage
    // Use separate key to avoid conflicts with proposal data
    const sessionData = sessionStorage.getItem("scopingai_generated_document") || sessionStorage.getItem("scopingai_proposal_data");
    let hasExistingDocument = false;

    if (sessionData) {
      try {
        const parsedData = JSON.parse(sessionData);
        hasExistingDocument = !!(
          parsedData.proposal &&
          parsedData.proposal.sections &&
          parsedData.proposal.sections.length > 0
        );
        console.log("📄 Existing document check:", {
          hasExistingDocument,
          sectionsCount: parsedData.proposal?.sections?.length || 0,
        });
      } catch (error) {
        console.error("Error checking existing document:", error);
      }
    }

    // Also check if document is already loaded in context
    const hasDocumentInContext =
      editableSections && editableSections.length > 0;

    // Only start live generation if there's no existing document
    if (
      isLiveGeneration &&
      !isLiveGenerating &&
      user?.id &&
      !hasExistingDocument &&
      !hasDocumentInContext
    ) {
      setIsLiveGenerating(true);
      setLiveGenerationProgress(1);
      setLiveGenerationStatus([]);

      console.log("🌊 Starting live generation for:", {
        client: clientFromURL,
        project: projectFromURL,
      });

      // Get knowledge base documents and requirements from session storage
      const sessionData = sessionStorage.getItem("scopingai_proposal_data");
      let selectedKnowledgeDocuments: string[] = [];
      let documentRequirements: Record<string, string> = {};

      if (sessionData) {
        try {
          const parsedData = JSON.parse(sessionData);
          selectedKnowledgeDocuments =
            parsedData.selectedKnowledgeDocuments || [];
          documentRequirements = parsedData.documentRequirements || {};

          console.log("📚 Knowledge base documents for live generation:", {
            selectedKnowledgeDocuments,
            documentRequirements,
          });
        } catch (error) {
          console.error("Error parsing session data:", error);
        }
      }

      // Start the workflow via POST request
      const startLiveGeneration = async () => {
        try {
          const workflowResponse = await fetch(
            "/api/tender/proposals/stream",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-api-key": "scopingai",
                "x-user-id": user?.id || "",
              },
              body: JSON.stringify({
                userId: user?.id,
                client: {
                  name: clientFromURL || "Client",
                  industry: "Technology",
                  email: "",
                  phone: "",
                  website: "",
                  description: `Live generation client: ${clientFromURL}`,
                },
                project: {
                  title: projectFromURL || "Project",
                  description: "Live generated proposal",
                  scope: "Comprehensive proposal generation",
                  timeline: "To be determined",
                  budget_range: "To be discussed",
                  requirements: [],
                  deliverables: [],
                },
                template: {
                  id: "live_generation_template",
                  name: "Live Generation Template",
                  sections: editableSections.map((s) => s.title),
                },
                requirements: {
                  type: "live_generation",
                  live_streaming: true,
                  use_knowledge_base_only:
                    selectedKnowledgeDocuments.length > 0,
                },
                selected_knowledge_documents: selectedKnowledgeDocuments,
                document_requirements: documentRequirements,
                aiPrompts: {
                  customPrompt: {
                    content:
                      selectedKnowledgeDocuments.length > 0
                        ? "Generate content using ONLY the data from selected knowledge base documents based on their aiExtractionInstructions. No hallucination - use only the extracted content."
                        : "Generate high-quality proposal content for live streaming",
                  },
                  style_guidance: "Professional and comprehensive",
                  content_focus: "Business value and technical accuracy",
                },
                config: {
                  quality_threshold: 0.8,
                  enable_live_streaming: true,
                  use_knowledge_base_only:
                    selectedKnowledgeDocuments.length > 0,
                  fast_generation: true,
                  stream_sections: true,
                  no_hallucination: selectedKnowledgeDocuments.length > 0,
                },
              }),
            }
          );

          if (!workflowResponse.ok) {
            throw new Error(
              `Workflow request failed: ${workflowResponse.status}`
            );
          }

          // Set up streaming response reader
          const reader = workflowResponse.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) {
            throw new Error("Failed to get response reader");
          }

          // Read the stream
          const readStream = async () => {
            try {
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split("\n");

                for (const line of lines) {
                  if (line.startsWith("data: ")) {
                    try {
                      const data = JSON.parse(line.slice(6));
                      console.log("📡 Live generation event:", data);

                      // Handle section generation
                      if (
                        data.section &&
                        data.section.title &&
                        data.section.content
                      ) {
                        setLiveGeneratedSections((prev) => ({
                          ...prev,
                          [data.section.title]: data.section.content,
                        }));

                        // Update the document sections in real-time
                        const sectionToUpdate = editableSections.find(
                          (s) => s.title === data.section.title
                        );
                        if (sectionToUpdate) {
                          handleUpdateSectionContent(
                            sectionToUpdate.id,
                            data.section.content
                          );
                        }

                        // Update status
                        setLiveGenerationStatus((prev) => [
                          ...prev,
                          data.section.title,
                        ]);
                      }

                      // Handle progress updates
                      if (data.progress) {
                        setLiveGenerationProgress(data.progress);
                      }

                      // Handle completion
                      if (data.status === "completed" || data.workflow_id) {
                        setIsLiveGenerating(false);
                        console.log("🎉 Live generation completed!");

                        // 🚨 FIX: Set completion flag for reliable auto-save triggering
                        setHasGenerationCompleted(true);

                        // Calculate actual section count from documentData
                        const generatedSectionCount = (documentData.sections && Array.isArray(documentData.sections)) ? documentData.sections.length : 0;

                        toast({
                          title: "Live Generation Complete!",
                          description: `Successfully generated ${generatedSectionCount} sections${
                            selectedKnowledgeDocuments.length > 0
                              ? " using knowledge base data"
                              : ""
                          }.`,
                        });

                        break;
                      }

                      // Handle errors
                      if (data.error) {
                        setIsLiveGenerating(false);
                        console.error("❌ Live generation error:", data.error);

                        toast({
                          title: "Generation Error",
                          description:
                            data.details ||
                            "An error occurred during generation.",
                          variant: "destructive",
                        });
                        break;
                      }
                    } catch (parseError) {
                      console.error(
                        "Error parsing streaming data:",
                        parseError
                      );
                    }
                  }
                }
              }
            } catch (streamError) {
              console.error("Stream reading error:", streamError);
              setIsLiveGenerating(false);

              toast({
                title: "Connection Error",
                description:
                  "Lost connection to live generation. Please refresh the page.",
                variant: "destructive",
              });
            }
          };

          readStream();
        } catch (error) {
          console.error("Failed to start live generation:", error);
          setIsLiveGenerating(false);

          toast({
            title: "Failed to Start Generation",
            description: "Could not connect to the live generation service.",
            variant: "destructive",
          });
        }
      };

      startLiveGeneration();

      // Cleanup function
      return () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      };
    }
  }, [
    isLiveGeneration,
    user?.id,
    clientFromURL,
    projectFromURL,
    editableSections,
    handleUpdateBlock,
    toast,
    isLiveGenerating, // Add this to prevent infinite loops
  ]);

  // Logo display check with support for both Supabase URLs and data URLs
  const documentLogo = documentTheme?.logo;

  // Utility function to validate logo sources (supports both URL types)
  const isValidLogoSrc = (src: any): src is string => {
    if (!src || typeof src !== "string" || src.length === 0) {
      return false;
    }

    // Support HTTP/HTTPS URLs (Supabase, CDN, etc.)
    if (src.startsWith("https://") || src.startsWith("http://")) {
      return true;
    }

    // Support data URLs (data:image/png;base64,... or data:image/jpeg;base64,...)
    if (src.startsWith("data:image/")) {
      return true;
    }

    // Support blob URLs (blob:...)
    if (src.startsWith("blob:")) {
      return true;
    }

    // Support relative URLs or other valid image sources
    return true;
  };

  const hasHeaderLogo = isValidLogoSrc(documentLogo);
  const logoSrc = hasHeaderLogo ? documentLogo : "";
  const logoOpacity = 1;
  const logoFilter = "none";

  // Only log if there are issues (silent success)
  if (documentLogo && !hasHeaderLogo) {
    console.warn("⚠️ DocumentsGenerated: Invalid logo source detected");
  }

  return (
    <>
      <div className="px-16 py-6 w-full">
        <header className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/tender/proposals">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Link>
              </Button>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">
                  {documentData.title || titleFromURL || "Generated Document"}
                </h1>
                {hasUnsavedChanges && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                    Unsaved Changes
                  </div>
                )}
              </div>
              {/* Logo Display in Header */}
              {hasHeaderLogo && logoSrc && (
                <div className="ml-auto">
                  <img
                    src={logoSrc}
                    alt="Logo"
                    className="h-8 object-contain"
                    style={{
                      opacity: logoOpacity,
                      filter: logoFilter,
                    }}
                    onError={(e) => {
                      // Silently handle logo load errors - hide the logo if it fails to load
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsSharingModalOpen(true)}
                disabled={isViewOnly}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Download className="h-4 w-4" />
                    Download
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  {/* PDF Download - DISABLED FOR NOW */}
                  <DropdownMenuItem
                    className="cursor-not-allowed flex flex-col items-start p-3 opacity-50"
                    disabled={true}
                  >
                    <div className="flex items-center w-full">
                      <FileText className="h-4 w-4 mr-2 text-gray-400" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-500">
                          High-Quality PDF
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Coming soon - PDF export temporarily disabled
                        </div>
                      </div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDownloadDOCX}
                    className="cursor-pointer flex flex-col items-start p-3"
                    disabled={isGeneratingPDF || isGeneratingDOCX}
                  >
                    <div className="flex items-center w-full">
                      {isGeneratingDOCX ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileText className="h-4 w-4 mr-2 text-blue-600" />
                      )}
                      <div className="flex-1">
                        <div className="font-medium">
                          {isGeneratingDOCX
                            ? "Generating DOCX..."
                            : "Advanced Word Document"}
                        </div>
                        {!isGeneratingDOCX && (
                          <div className="text-xs text-muted-foreground">
                            Editable DOCX with perfect markdown conversion
                          </div>
                        )}
                      </div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        const filename = `${documentData.title?.replace(/[^a-z0-9]/gi, "_") || "document"}.html`;
                        const exportDocument = {
                          title: documentData.title || "Document",
                          client: documentData.client,
                          sections: (editableSections || documentData.sections || []).map(section => ({
                            id: section.id,
                            title: section.title,
                            content: section.content || ""
                          })),
                          theme: {
                            logo: unifiedBrandedTheme?.logo,
                            brandName: unifiedBrandedTheme?.brandName,
                            brandTagline: unifiedBrandedTheme?.brandTagline,
                            headingColor: unifiedBrandedTheme?.headingColor,
                            textColor: unifiedBrandedTheme?.textColor,
                            accentColor: unifiedBrandedTheme?.accentColor,
                            fontFamily: unifiedBrandedTheme?.fontFamily,
                          }
                        };
                        // 🚨 FIXED: Professional HTML export with proper markdown conversion
                        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <title>${exportDocument.title}</title>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          max-width: 800px;
          margin: 0 auto;
          padding: 40px 20px;
          line-height: 1.6;
          color: #1f2937;
          background: #ffffff;
        }
        h1 {
          color: #111827;
          border-bottom: 3px solid #3b82f6;
          padding-bottom: 10px;
          margin-bottom: 30px;
          font-size: 2.5em;
          font-weight: 700;
        }
        h2 {
          color: #374151;
          border-left: 4px solid #3b82f6;
          padding-left: 15px;
          margin-top: 40px;
          margin-bottom: 20px;
          font-size: 1.8em;
          font-weight: 600;
        }
        h3 {
          color: #4b5563;
          margin-top: 30px;
          margin-bottom: 15px;
          font-size: 1.4em;
          font-weight: 600;
        }
        p { margin-bottom: 16px; }
        ul, ol { margin-bottom: 16px; padding-left: 30px; }
        li { margin-bottom: 8px; }
        strong { color: #111827; font-weight: 600; }
        em { color: #4b5563; }
        code {
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.9em;
        }
        blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 20px;
          margin: 20px 0;
          color: #6b7280;
          font-style: italic;
        }
        .section {
          margin-bottom: 50px;
          padding-bottom: 30px;
          border-bottom: 1px solid #e5e7eb;
        }
        .section:last-child {
          border-bottom: none;
        }
        .client-info {
          background: #f8fafc;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
          border: 1px solid #e2e8f0;
        }
    </style>
</head>
<body>
    <h1>${exportDocument.title}</h1>
    ${exportDocument.client ? `<div class="client-info"><strong>Client:</strong> ${exportDocument.client}</div>` : ''}
    ${exportDocument.sections.map(section => `
        <div class="section">
            <h2>${section.title}</h2>
            <div>${convertMarkdownToHTML(section.content || "")}</div>
        </div>
    `).join('')}
</body>
</html>`;
                        const blob = new Blob([htmlContent], { type: 'text/html' });
                        const { saveAs } = await import('file-saver');
                        saveAs(blob, filename);
                        toast({
                          title: "HTML Generated",
                          description: "Standalone HTML file downloaded successfully.",
                        });
                      } catch (error) {
                        toast({
                          title: "Export Failed",
                          description: "Failed to generate HTML file.",
                          variant: "destructive",
                        });
                      }
                    }}
                    className="cursor-pointer flex flex-col items-start p-3"
                  >
                    <div className="flex items-center w-full">
                      <FileText className="h-4 w-4 mr-2 text-green-600" />
                      <div className="flex-1">
                        <div className="font-medium">Standalone HTML</div>
                        <div className="text-xs text-muted-foreground">
                          Self-contained HTML file with embedded styles
                        </div>
                      </div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        const filename = `${documentData.title?.replace(/[^a-z0-9]/gi, "_") || "document"}.md`;
                        const exportDocument = {
                          title: documentData.title || "Document",
                          client: documentData.client,
                          sections: (editableSections || documentData.sections || []).map(section => ({
                            id: section.id,
                            title: section.title,
                            content: section.content || ""
                          })),
                          theme: unifiedBrandedTheme
                        };
                        // Simple markdown export
                        let markdown = `# ${exportDocument.title}\n\n`;
                        if (exportDocument.client) {
                          markdown += `**Client:** ${exportDocument.client}\n\n`;
                        }
                        markdown += `---\n\n`;
                        for (const section of exportDocument.sections) {
                          markdown += `## ${section.title}\n\n${section.content}\n\n---\n\n`;
                        }
                        const blob = new Blob([markdown], { type: 'text/markdown' });
                        const { saveAs } = await import('file-saver');
                        saveAs(blob, filename);
                        toast({
                          title: "Markdown Generated",
                          description: "Clean markdown file downloaded successfully.",
                        });
                      } catch (error) {
                        toast({
                          title: "Export Failed",
                          description: "Failed to generate markdown file.",
                          variant: "destructive",
                        });
                      }
                    }}
                    className="cursor-pointer flex flex-col items-start p-3"
                  >
                    <div className="flex items-center w-full">
                      <FileText className="h-4 w-4 mr-2 text-purple-600" />
                      <div className="flex-1">
                        <div className="font-medium">Clean Markdown</div>
                        <div className="text-xs text-muted-foreground">
                          Pure markdown format for editing and sharing
                        </div>
                      </div>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {canEdit && (
                <Button
                  onClick={() => {
                    debouncedSave(saveDocumentToSupabase);
                    // Force immediate preview update
                    setPreviewKey(prev => prev + 1);
                  }}
                  disabled={isSaving || isSavingDebounced || isAutoSaving || !documentData.title.trim()}
                  size="sm"
                  className={`gap-2 ${hasUnsavedChanges ? 'bg-orange-600 hover:bg-orange-700' : ''}`}
                >
                  {(isSaving || isSavingDebounced) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {isAutoSaving
                    ? "Auto-Saving..."
                    : isSaving
                    ? "Saving..."
                    : isSavingDebounced
                    ? "Preparing..."
                    : !documentData.title.trim()
                    ? "Title Required"
                    : hasUnsavedChanges
                    ? "Save Changes"
                    : documentDbId || autoSaveAttempted || isFromProposalsPage
                    ? "Update"
                    : "Save"}
                </Button>
              )}

              {isViewOnly && (
                <div className="flex items-center px-3 py-1 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm">
                  <Eye className="h-4 w-4 mr-2" />
                  View Only
                </div>
              )}
            </div>
          </div>
        </header>

        <Tabs
          defaultValue="preview"
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <TabsList className="mb-4 relative z-10">
            <TabsTrigger value="preview">
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </TabsTrigger>
            {canEdit && (
              <TabsTrigger value="editor" className="relative">
                <Edit3 className="h-4 w-4 mr-2" />
                Content Editor
                {preserveOriginal && (
                  <div className="absolute -top-1 -right-1">
                    <Badge
                      variant="outline"
                      className="text-[0.6rem] h-4 px-1 bg-amber-50 text-amber-800 border-amber-200"
                    >
                      Reference
                    </Badge>
                  </div>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger value="metadata">
              <FileText className="h-4 w-4 mr-2" />
              Document Info
            </TabsTrigger>
            {canEdit && (
              <TabsTrigger value="theme">
                <Sparkles className="h-4 w-4 mr-2" />
                Theme & Branding
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="preview" className="pt-4">
            {isLoading ? (
              <Card>
                <CardContent className="p-6 flex justify-center">
                  <div className="animate-pulse flex flex-col space-y-4 w-full">
                    <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Settings button positioned at the right end of the preview tab, outside the document */}
                <div className="flex justify-end mb-2 gap-2 relative mt-[-76px]">
                  {/* Settings Button */}
                </div>

                {/* Document Preview */}
                <div className="flex gap-6">
                  <div ref={documentContainerRef} className="flex-1">
                    {(() => {
                      // Debug preview content
                      console.log('🔍 Preview Tab - Rendering with data:', {
                        sectionsCount: editableSections?.length || 0,
                        previewKey,
                        sectionsWithImages: editableSections?.filter(s => s.content?.includes('![')).length || 0,
                        sampleSectionContent: editableSections?.[0]?.content?.substring(0, 100) + '...' || 'No content',
                        hasImages: editableSections?.some(s => s.content?.includes('![')) || false
                      });

                      // 🚨 CRITICAL DEBUG: Show ALL sections being passed to preview
                      console.log('🔍 ALL SECTIONS BEING PASSED TO PREVIEW:', {
                        editableSectionsCount: editableSections?.length || 0,
                        editableSectionIds: editableSections?.map(s => s.id) || [],
                        editableSectionTitles: editableSections?.map(s => s.title) || [],
                        pagesCount: pages?.length || 0,
                        pagesData: pages?.map((page, index) => ({
                          pageIndex: index,
                          sectionsInPage: page.content?.length || 0,
                          sectionIdsInPage: page.content?.map(s => s.id) || [],
                          sectionTitlesInPage: page.content?.map(s => s.title) || []
                        })) || []
                      });
                      
                      // Sample image content for debugging
                      editableSections?.forEach((section, index) => {
                        if (section.content?.includes('![')) {
                          console.log(`🖼️ Preview Tab - Section ${index + 1} (${section.title}) has images:`, {
                            contentPreview: section.content.substring(0, 200) + '...',
                            imageMatches: section.content.match(/!\[([^\]]*)\]\(([^)]+)\)/g) || []
                          });
                        }
                      });
                      
                      return null;
                    })()}
                    <PDFLikePreview
                      key={`preview-${previewKey}`}
                      sections={editableSections || []}
                      documentTitle={documentData?.title || "Document Preview"}
                      clientData={{
                        company: documentData?.client || "Client Company",
                        name: documentData?.clientName,
                        email: documentData?.clientEmail,
                      }}
                      projectData={{
                        title: documentData?.title || "Project Title",
                        description: documentData?.description,
                      }}
                      onSectionClick={(sectionId) => {
                        setSelectedSectionId(sectionId);
                      }}
                      selectedSectionId={selectedSectionId}
                      showSidebar={true}

                      isEditMode={false}
                    />
                  </div>

                  {/* Settings Panel */}
                </div>
              </>
            )}
          </TabsContent>

          {/* Yoopta-Based Content Editor Tab */}
          <TabsContent value="editor" className="pt-4">
            {/* 🚨 NEW: Regeneration Controls */}
            {canEdit && !isViewOnly && (
              <div className="flex flex-col gap-2 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex gap-2">
                  <Button
                    onClick={handleRegenerateAll}
                    disabled={regenerationProgress.isGenerating || isRegeneratingWithPrompt}
                    className="gap-2 bg-primary hover:bg-primary/90"
                    size="sm"
                  >
                    {(regenerationProgress.isGenerating || isRegeneratingWithPrompt) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Regenerate All
                  </Button>
                  <div className="text-sm text-gray-600 flex items-center">
                    Use "Regenerate All" to regenerate the entire document, or click "Regenerate Section" on individual sections below.
                  </div>
                </div>

                {/* 🚨 NEW: Show regeneration progress */}
                {(regenerationProgress.isGenerating || isRegeneratingWithPrompt) && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">
                        {regenerationProgress.currentStep || "Regenerating..."}
                      </span>
                    </div>
                    {regenerationProgress.progress > 0 && (
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${regenerationProgress.progress}%` }}
                        ></div>
                      </div>
                    )}
                    {regenerationProgress.message && (
                      <p className="text-xs text-blue-600 mt-1">{regenerationProgress.message}</p>
                    )}
                  </div>
                )}
              </div>
            )}



            {/* Section Management Layout */}
            <div className="flex gap-6 h-full">
              {/* Left Sidebar - Sortable Section List */}
              <div className="w-80 flex-shrink-0 border-r border-gray-200 bg-gray-50">
                <div className="h-full flex flex-col">
                  {/* Header */}
                  <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-white">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900">
                        Document Sections ({editableSections.length})
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newSectionId = `section-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
                          const newSection = {
                            id: newSectionId,
                            title: "New Section".trim(),
                            content: "",
                            blocks: [],
                            status: "draft" as const,
                            level: 1,
                            order: editableSections.length,
                          };

                          setEditableSections(prev => [...prev, newSection]);
                          setSelectedSectionId(newSectionId);

                          toast({
                            title: "Section Added",
                            description: "New section added successfully",
                            duration: 2000,
                          });
                        }}
                        className="gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add Section
                      </Button>
                    </div>
                  </div>

                  {/* Section List */}
                  <div className="flex-1 overflow-y-auto">
                    <div className="p-2">
                      {editableSections.length > 0 ? (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={editableSections.map((section) => section.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {editableSections.map((section) => (
                              <SortableSection
                                key={section.id}
                                section={section}
                                selectedSectionId={selectedSectionId}
                                editingTitles={editingTitles}
                                titleInputs={titleInputs}
                                onSectionClick={(sectionId) => {
                                  setSelectedSectionId(sectionId);
                                }}
                                onTitleChange={(sectionId, value) => {
                                  setTitleInputs(prev => ({ ...prev, [sectionId]: value }));
                                }}
                                onTitleSave={handleTitleSave}
                                onTitleCancel={handleTitleCancel}
                                onEditClick={(sectionId) => {
                                  setSelectedSectionId(sectionId);
                                }}
                                onToggleTitleEditing={handleTitleEdit}
                                onAddSectionAfter={handleAddSectionAfter}
                                onSectionRegenerate={(sectionId, sectionTitle) => {
                                  console.log("🔄 Starting section regeneration from sortable list:", { sectionId, sectionTitle });

                                  // 🚨 FIXED: Ensure clean state before opening dialog
                                  if (showPromptDialog) {
                                    console.log("🚫 Dialog already open, ignoring duplicate request");
                                    return;
                                  }

                                  // Reset any existing state
                                  setCustomPrompt("");
                                  setIsRegeneratingWithPrompt(false);

                                  // Set new state
                                  setPendingRegenerationType('section');
                                  setPendingSectionId(sectionId);
                                  setPendingSectionTitle(sectionTitle);

                                  // 🚨 NEW: Remember which section we're regenerating
                                  setSelectedSectionId(sectionId);

                                  // Small delay to ensure state is set
                                  setTimeout(() => {
                                    setShowPromptDialog(true);
                                  }, 50);
                                }}
                                onDeleteSection={handleDeleteSection}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p>No sections available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Content - Section Editor */}
              <div className="flex-1">
                <YooptaContentEditor
                  key={`editor-${editorMountKey}`}
                  sections={editableSections}
                  readonly={isViewOnly}
                  userId={user?.id}
                  initialSelectedSectionId={selectedSectionId || undefined}
                  hideSectionList={true}
                  onSectionSelect={(sectionId) => {
                    setSelectedSectionId(sectionId);
                  }}
              onSectionDelete={(sectionId) => {
                // Handle section deletion
                setEditableSections((prev) => {
                  const updatedSections = prev.filter((section) => section.id !== sectionId);
                  return updatedSections;
                });
                toast({
                  title: "Section Deleted",
                  description: "The section has been removed successfully.",
                });
              }}
              onSectionAdd={() => {
                const newSectionId = `section-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                const newSection = {
                  id: newSectionId,
                  title: "New Section",
                  content: "",
                  level: 1,
                  order: editableSections.length,
                };

                setEditableSections(prev => [...prev, newSection]);
                
                toast({
                  title: "Section Added",
                  description: "New section added successfully",
                  duration: 2000,
                });
              }}
              onSectionAddAfter={(afterSectionId) => {
                const afterSectionIndex = editableSections.findIndex(s => s.id === afterSectionId);
                if (afterSectionIndex === -1) return;

                const newSectionId = `section-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                const newSection = {
                  id: newSectionId,
                  title: "New Section",
                  content: "",
                  level: 1,
                  order: afterSectionIndex + 1,
                };

                setEditableSections(prev => {
                  const updatedSections = [...prev];
                  updatedSections.splice(afterSectionIndex + 1, 0, newSection);
                  
                  // Update order for sections after the insertion point
                  updatedSections.forEach((section, index) => {
                    if (index > afterSectionIndex) {
                      section.order = index;
                    }
                  });
                  
                  return updatedSections;
                });

                toast({
                  title: "Section Added",
                  description: "New section added after the selected section",
                  duration: 2000,
                });
              }}
              onSectionTitleChange={(sectionId, newTitle) => {
                console.log('🚨 DocumentsGenerated onSectionTitleChange called (LOCAL UPDATE ONLY):', {
                  sectionId,
                  newTitle,
                  currentSectionsCount: editableSections?.length || 0
                });

                // 🚨 FIXED: Only update local state, DO NOT save to database automatically
                setEditableSections((prev) => {
                  const newSections = prev.map((section) =>
                    section.id === sectionId ? { ...section, title: newTitle } : section
                  );
                  console.log('🚨 DocumentsGenerated AFTER title update (LOCAL ONLY):', {
                    sectionId,
                    newTitle,
                    newSectionsCount: newSections.length,
                    updatedSectionInNew: newSections.find(s => s.id === sectionId)
                  });
                  return newSections;
                });

                // Force preview re-render
                setPreviewKey(prev => prev + 1);

                // Don't mark as unsaved - title changes are handled as part of section saves
                // This prevents the unsaved flag from being set after a save operation

                // 🚨 FIXED: Title changes are part of section saves
                console.log('✅ Section title updated as part of save operation.');
              }}
              onSectionUpdate={async (sectionId, updatedSection) => {
                const timestamp = new Date().toISOString();
                console.log(`🚨 [${timestamp}] DocumentsGenerated onSectionUpdate called:`, {
                  sectionId,
                  updatedSectionContent: updatedSection.content?.length || 0,
                  hasContent: !!(updatedSection.content && updatedSection.content.trim()),
                  currentHasUnsavedChanges: hasUnsavedChanges
                });

                // Update local state first
                console.log('🚨 DocumentsGenerated BEFORE setEditableSections:', {
                  sectionId,
                  currentSectionsCount: editableSections?.length || 0,
                  updatedSectionContent: updatedSection.content?.length || 0
                });

                setEditableSections((prev) => {
                  const newSections = prev.map((section) =>
                    section.id === sectionId ? updatedSection : section
                  );
                  console.log('🚨 DocumentsGenerated AFTER setEditableSections:', {
                    sectionId,
                    newSectionsCount: newSections.length,
                    updatedSectionInNew: newSections.find(s => s.id === sectionId),
                    allSectionIds: newSections.map(s => s.id)
                  });
                  return newSections;
                });

                // Force preview re-render
                setPreviewKey(prev => prev + 1);

                // Clear unsaved changes flag since section is now updated
                const timestamp2 = new Date().toISOString();
                console.log(`🚨 [${timestamp2}] SECTION UPDATE - Calling updateHasUnsavedChanges(false)`);
                updateHasUnsavedChanges(false);

                // Add a timeout to check if the flag was actually cleared
                setTimeout(() => {
                  console.log(`🚨 [${new Date().toISOString()}] SECTION - hasUnsavedChanges after 100ms:`, {
                    state: hasUnsavedChanges,
                    ref: hasUnsavedChangesRef.current
                  });
                }, 100);

                // 🚨 FIXED: Section saved through YooptaContentEditor
                console.log('✅ Section updated and saved through YooptaContentEditor.');
              }}
              onPreviewUpdate={(sectionId, updatedSection) => {
                console.log('🚨 DocumentsGenerated onPreviewUpdate called:', {
                  sectionId,
                  updatedSectionContent: updatedSection.content?.length || 0,
                  hasContent: !!(updatedSection.content && updatedSection.content.trim()),
                  hasImages: !!(updatedSection.content && updatedSection.content.includes('!['))
                });

                // Update preview immediately without saving to database
                console.log('🚨 DocumentsGenerated onPreviewUpdate BEFORE setEditableSections:', {
                  sectionId,
                  currentSectionsCount: editableSections?.length || 0,
                  updatedSectionContent: updatedSection.content?.length || 0
                });

                setEditableSections((prev) => {
                  const newSections = prev.map((section) =>
                    section.id === sectionId ? updatedSection : section
                  );
                  console.log('🚨 DocumentsGenerated onPreviewUpdate AFTER setEditableSections:', {
                    sectionId,
                    newSectionsCount: newSections.length,
                    updatedSectionInNew: newSections.find(s => s.id === sectionId),
                    allSectionIds: newSections.map(s => s.id)
                  });
                  return newSections;
                });

                // Force preview re-render
                setPreviewKey(prev => prev + 1);
              }}
              onSectionRegenerate={(sectionId, sectionTitle) => {
                console.log("🔄 Section regeneration requested from YooptaContentEditor:", {
                  sectionId,
                  sectionTitle,
                  currentDialogState: showPromptDialog,
                  isRegenerating: isRegeneratingWithPrompt
                });

                // 🚨 FIXED: Prevent multiple calls if already processing
                if (showPromptDialog || isRegeneratingWithPrompt) {
                  console.log("🚫 Already processing regeneration, ignoring duplicate request");
                  return;
                }

                handleRegenerateSection(sectionId, sectionTitle);
              }}
            />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="metadata" className="pt-4">
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">
                      Document Title
                    </label>
                    <div className="mt-1">
                      {isEditingProjectTitle ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={projectTitleInput}
                            onChange={(e) => handleProjectTitleChange(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleProjectTitleSave();
                              } else if (e.key === "Escape") {
                                handleProjectTitleCancel();
                              }
                            }}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            autoFocus
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleProjectTitleSave}
                            className="h-6 px-2"
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleProjectTitleCancel}
                            className="h-6 px-2"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group">
                          <p className="text-sm text-muted-foreground">
                            {documentData.title}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={toggleProjectTitleEditing}
                            className="h-6 px-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Edit3 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Client</label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {documentData.client}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Document Type</label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {documentData.type}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Pages</label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {pages?.length || 1} pages
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Sections</label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {documentData.sections?.length || 0} sections total
                    </p>
                  </div>
                  {hasHeaderLogo && logoSrc && (
                    <div>
                      <label className="text-sm font-medium">Logo</label>
                      <div className="mt-2 flex items-center gap-3">
                        <div className="relative">
                          <img
                            src={logoSrc}
                            alt="Document Logo"
                            className="h-12 object-contain border rounded"
                            onError={(e) => {
                              // Silently handle logo load errors - show fallback
                              const target = e.currentTarget;
                              target.style.display = "none";
                              const fallback =
                                target.nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = "flex";
                            }}
                          />
                          <div
                            className="h-12 w-12 border-2 border-dashed border-muted-foreground/25 rounded flex items-center justify-center"
                            style={{ display: "none" }}
                          >
                            <div className="text-center">
                              <svg
                                className="h-4 w-4 mx-auto text-muted-foreground/50"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <p>Logo is enabled for this document</p>
                          <p className="text-xs mt-1">
                            Type:{" "}
                            {logoSrc.startsWith("data:image/")
                              ? "Uploaded Image"
                              : "URL Image"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {preserveOriginal && (
                    <div>
                      <label className="text-sm font-medium">Mode</label>
                      <Badge
                        variant="outline"
                        className="ml-2 bg-amber-50 text-amber-800 border-amber-200"
                      >
                        Reference Document
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="theme" className="pt-4">
            <Card>
              <CardContent className="p-8 md:p-12">
                {/* Two-Column Layout: Branding Settings (left) & Org Branding (right) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 min-h-[420px]">
                  {/* Left: Document Branding Settings */}
                  <div className="flex flex-col h-full bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
                    <h3 className="text-xl font-bold mb-2 text-primary tracking-tight">Document Branding Settings</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Customize colors and fonts for this document only. These changes won't affect your organization's main branding.
                    </p>
                    <div className="space-y-6 flex-1">
                      {/* Primary Color */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Primary Color (Headings)</label>
                        <div className="flex gap-2 items-center">
                          <input
                            type="color"
                            value={documentBrandingOverrides.primary_color || organizationBranding?.primary_color || "#1a1a1a"}
                            onChange={(e) => setDocumentBrandingOverrides((prev) => ({ ...prev, primary_color: e.target.value }))}
                            className="w-10 h-10 border rounded cursor-pointer shadow-sm"
                          />
                          <input
                            type="text"
                            value={documentBrandingOverrides.primary_color || organizationBranding?.primary_color || "#1a1a1a"}
                            onChange={(e) => setDocumentBrandingOverrides((prev) => ({ ...prev, primary_color: e.target.value }))}
                            className="flex-1 px-3 py-2 border rounded text-sm bg-gray-50 focus:ring-2 focus:ring-primary/20"
                            placeholder={organizationBranding?.primary_color || "#1a1a1a"}
                          />
                        </div>
                        {organizationBranding?.primary_color && (
                          <p className="text-xs text-muted-foreground mt-1">Organization default: {organizationBranding.primary_color}</p>
                        )}
                      </div>
                      {/* Secondary Color */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Secondary Color (Body Text)</label>
                        <div className="flex gap-2 items-center">
                          <input
                            type="color"
                            value={documentBrandingOverrides.secondary_color || organizationBranding?.secondary_color || "#333333"}
                            onChange={(e) => setDocumentBrandingOverrides((prev) => ({ ...prev, secondary_color: e.target.value }))}
                            className="w-10 h-10 border rounded cursor-pointer shadow-sm"
                          />
                          <input
                            type="text"
                            value={documentBrandingOverrides.secondary_color || organizationBranding?.secondary_color || "#333333"}
                            onChange={(e) => setDocumentBrandingOverrides((prev) => ({ ...prev, secondary_color: e.target.value }))}
                            className="flex-1 px-3 py-2 border rounded text-sm bg-gray-50 focus:ring-2 focus:ring-primary/20"
                            placeholder={organizationBranding?.secondary_color || "#333333"}
                          />
                        </div>
                        {organizationBranding?.secondary_color && (
                          <p className="text-xs text-muted-foreground mt-1">Organization default: {organizationBranding.secondary_color}</p>
                        )}
                      </div>
                      {/* Font Color */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Font Color (Default Text)</label>
                        <div className="flex gap-2 items-center">
                          <input
                            type="color"
                            value={documentBrandingOverrides.font_color || organizationBranding?.font_color || "#1F2937"}
                            onChange={(e) => setDocumentBrandingOverrides((prev) => ({ ...prev, font_color: e.target.value }))}
                            className="w-10 h-10 border rounded cursor-pointer shadow-sm"
                          />
                          <input
                            type="text"
                            value={documentBrandingOverrides.font_color || organizationBranding?.font_color || "#1F2937"}
                            onChange={(e) => setDocumentBrandingOverrides((prev) => ({ ...prev, font_color: e.target.value }))}
                            className="flex-1 px-3 py-2 border rounded text-sm bg-gray-50 focus:ring-2 focus:ring-primary/20"
                            placeholder={organizationBranding?.font_color || "#1F2937"}
                          />
                        </div>
                        {organizationBranding?.font_color && (
                          <p className="text-xs text-muted-foreground mt-1">Organization default: {organizationBranding.font_color}</p>
                        )}
                      </div>
                      {/* Font Family - Dropdown */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Font Family</label>
                        <div className="relative">
                          <select
                            value={documentBrandingOverrides.font_family || "Inter, system-ui, -apple-system, sans-serif"}
                            onChange={(e) => setDocumentBrandingOverrides((prev) => ({ ...prev, font_family: e.target.value }))}
                            className="w-full px-4 py-2 pr-10 border rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-primary/20 appearance-none shadow-sm font-medium"
                            style={{ fontFamily: documentBrandingOverrides.font_family || "Inter, system-ui, -apple-system, sans-serif" }}
                          >
                            <option value="Inter, system-ui, -apple-system, sans-serif" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>Inter</option>
                            <option value="Arial, Helvetica, sans-serif" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>Arial</option>
                            <option value="Helvetica, Arial, sans-serif" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>Helvetica</option>
                            <option value="'Times New Roman', Times, serif" style={{ fontFamily: 'Times New Roman, Times, serif' }}>Times New Roman</option>
                            <option value="Georgia, serif" style={{ fontFamily: 'Georgia, serif' }}>Georgia</option>
                            <option value="Roboto, Arial, sans-serif" style={{ fontFamily: 'Roboto, Arial, sans-serif' }}>Roboto</option>
                            <option value="'Open Sans', Arial, sans-serif" style={{ fontFamily: 'Open Sans, Arial, sans-serif' }}>Open Sans</option>
                          </select>
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
                            <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </span>
                        </div>
                        {organizationBranding?.font_family && (
                          <p className="text-xs text-muted-foreground mt-1">Organization default: {organizationBranding.font_family}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Right: Current Organization Branding (read-only, styled) */}
                  <div className="flex flex-col h-full bg-gray-50 rounded-2xl border border-gray-100 p-8 shadow-sm">
                    {/* Logo Preview */}
                    {organizationBranding?.logo_url && (
                      <div className="flex flex-col items-center mb-8">
                        <span className="text-base font-semibold text-muted-foreground mb-2 tracking-wide uppercase">Organization Logo</span>
                        <img
                          src={BrandingService.getLogoUrlWithCacheBusting(organizationBranding.logo_url)}
                          alt="Organization Logo"
                          className="h-40 object-contain border border-gray-200 rounded-xl shadow bg-white p-4 mb-2"
                          style={{ maxWidth: 280 }}
                        />
                      </div>
                    )}
                    <h4 className="text-xl font-bold mb-3 text-primary tracking-tight">Current Organization Branding</h4>
                    <div className="border-b border-dashed border-gray-200 mb-4"></div>
                    {isLoadingBranding ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        <span className="ml-2 text-sm text-muted-foreground">Loading branding...</span>
                      </div>
                    ) : organizationBranding ? (
                      <div className="flex flex-col gap-4 flex-1 justify-between text-sm">
                        <div className="space-y-2">
                          {organizationBranding.brand_title && (
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Brand Title:</span>
                              <span className="font-medium">{organizationBranding.brand_title}</span>
                            </div>
                          )}
                          {organizationBranding.tagline && (
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Tagline:</span>
                              <span className="font-medium">{organizationBranding.tagline}</span>
                            </div>
                          )}
                          {organizationBranding.primary_color && (
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Primary Color:</span>
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded border" style={{ backgroundColor: organizationBranding.primary_color }}></div>
                                <span className="font-mono text-xs">{organizationBranding.primary_color}</span>
                              </div>
                            </div>
                          )}
                          {organizationBranding.secondary_color && (
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Secondary Color:</span>
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded border" style={{ backgroundColor: organizationBranding.secondary_color }}></div>
                                <span className="font-mono text-xs">{organizationBranding.secondary_color}</span>
                              </div>
                            </div>
                          )}
                          {organizationBranding.font_color && (
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Font Color:</span>
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded border" style={{ backgroundColor: organizationBranding.font_color }}></div>
                                <span className="font-mono text-xs">{organizationBranding.font_color}</span>
                              </div>
                            </div>
                          )}
                          {organizationBranding.font_family && (
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Font Family:</span>
                              <span className="font-medium" style={{ fontFamily: organizationBranding.font_family }}>{organizationBranding.font_family}</span>
                            </div>
                          )}
                        </div>
                        {/* Fill space to match left column height */}
                        <div className="flex-1" />
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">No organization branding data available.</div>
                    )}
                  </div>
                </div>
                {/* Action Buttons (unchanged) */}
                <div className="flex gap-2 pt-10 mt-8 border-t border-dashed border-gray-200">
                  <Button
                    variant="outline"
                    onClick={resetDocumentBranding}
                    className="flex-1"
                  >
                    Reset to Organization Defaults
                  </Button>
                  <Button
                    onClick={() => {
                      // Apply changes and show toast
                      console.log(
                        "🔧 Document Branding Overrides Applied:",
                        documentBrandingOverrides
                      );

                      // Force re-render by updating a state that triggers unified theme recalculation
                      // The unified theme will automatically update due to the dependency on documentBrandingOverrides

                      toast({
                        title: "Document Branding Applied",
                        description:
                          "Your custom branding settings are now active for this document.",
                      });
                    }}
                    className="flex-1"
                  >
                    Apply Custom Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
        />
        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
        />
      </div>

      {/* Advanced Document Sharing Modal */}
      {/* 🚨 REMOVED: Method selection popup - now go directly to AI prompt */}

      {/* 🚨 NEW: AI Prompt Dialog (same as regeneration page) */}
      {showPromptDialog && (
        <Dialog
          open={showPromptDialog}
          onOpenChange={(open) => {
            console.log("🔄 Dialog onOpenChange:", { open, currentState: showPromptDialog });
            if (!open) {
              // Dialog is being closed
              setShowPromptDialog(false);
              setCustomPrompt("");
              setPendingRegenerationType(null);
              setPendingSectionId(null);
              setPendingSectionTitle(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {pendingRegenerationType === 'full'
                  ? 'Regenerate All Sections'
                  : `Regenerate "${pendingSectionTitle}"`}
              </DialogTitle>
              <DialogDescription>
                {pendingRegenerationType === 'full'
                  ? 'Provide custom instructions for regenerating all sections of this document.'
                  : `Provide custom instructions for regenerating the "${pendingSectionTitle}" section.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* 🚨 FIXED: Show progress when regenerating */}
              {(regenerationProgress.isGenerating || isRegeneratingWithPrompt) ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    <span className="font-medium text-blue-800">
                      {regenerationProgress.currentStep || "Regenerating..."}
                    </span>
                  </div>

                  {regenerationProgress.progress > 0 && (
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${regenerationProgress.progress}%` }}
                      ></div>
                    </div>
                  )}

                  {regenerationProgress.message && (
                    <p className="text-sm text-blue-600">{regenerationProgress.message}</p>
                  )}

                  <p className="text-xs text-gray-500">
                    Please wait while we regenerate your content...
                  </p>
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium">Custom Instructions</label>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Describe how you'd like the content to be regenerated..."
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                  />
                </div>
              )}
              {/* 🚨 FIXED: Hide buttons during regeneration */}
              {!(regenerationProgress.isGenerating || isRegeneratingWithPrompt) && (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      console.log("🚫 Canceling regeneration dialog");
                      setShowPromptDialog(false);
                      setCustomPrompt("");
                      // 🚨 FIXED: Reset all pending state on cancel
                      setPendingRegenerationType(null);
                      setPendingSectionId(null);
                      setPendingSectionTitle(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleRegenerateWithPrompt}
                    disabled={!customPrompt.trim()}
                    className="gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Regenerate
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <DocumentSharingModal
        isOpen={isSharingModalOpen}
        onClose={() => setIsSharingModalOpen(false)}
        documentId={documentDbId || searchParams?.get("id") || ""}
        documentTitle={
          documentData.title || titleFromURL || "Generated Document"
        }
        canManage={userPermission === null || userPermission === "admin"} // Only document owners can manage sharing
      />
    </>
  );
}

// Default export wrapped in Suspense and provider
export default function DocumentsGenerated() {
  return (
    <Suspense
      fallback={
        <div className="flex flex-col items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="mt-4 text-muted-foreground">Loading document...</p>
        </div>
      }
    >
      <DocumentProvider>
        <DocumentContent />
      </DocumentProvider>
    </Suspense>
  );
}
